import { hashName } from './security';

/**
 * Array of error messages indicating token-related issues
 */
const tokenError = [
  'invalid token',
  'invalidtoken',
  'invalid authentication',
  'invalidauthentication',
];

/**
 * Custom error class that includes additional data
 */
export class CustomError extends Error {
  constructor(public data: any) {
    super();
    this.name = this.constructor.name;
  }
}
/**
 * Normalize message to make comparison resilient
 * - removes extra spaces
 * - converts newlines to spaces
 * - lowercases for case-insensitive match
 */
const normalize = (msg: string): string =>
  msg.replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * Detect maintenance/system message
 */
const isMaintenanceMessage = (msg: string): boolean =>
  normalize(msg).includes(
    'we`re currently making some updates to improve your experience',
  );

/**
 * Sanitize API errors:
 * - remove maintenance message
 * - remove duplicates
 */
const sanitizeErrors = (errors: string[]): string[] => {
  const seen = new Set<string>();

  return errors.filter((msg) => {
    if (isMaintenanceMessage(msg)) return false;

    if (seen.has(msg)) return false;

    seen.add(msg);
    return true;
  });
};

/**
 * Handles API errors and returns appropriate error information
 * @param baseQueryReturnValue - The return value from the base query
 * @returns An object containing error message and error level
 */
export const handleError = (baseQueryReturnValue: any) => {
  if (!baseQueryReturnValue?.data || !baseQueryReturnValue?.status) {
    return {
      errMsg: 'Server Error',
      errLevel: 0,
      severity: 'error' as const,
    };
  }

  const status =
    baseQueryReturnValue?.status || baseQueryReturnValue?.data?.status;

  if (status >= 500 && status < 600) {
    return {
      errMsg:
        baseQueryReturnValue?.data?.message ||
        baseQueryReturnValue?.data ||
        'Server Error',
      errLevel: 500,
      severity: 'error' as const,
    };
  }

  // Normalize — support both wrapped (data.data) and flat (data) shapes
  const payload =
    baseQueryReturnValue?.data?.data ?? baseQueryReturnValue?.data;

  // Map errorLevel string to MUI severity
  const severity = (
    payload?.errorLevel === 'warning'
      ? 'warning'
      : payload?.errorLevel === 'info'
        ? 'info'
        : 'error'
  ) as 'error' | 'warning' | 'info';

  if (payload?.errors?.length > 0) {
    const arrError = payload?.errors?.map(({ error }: any) => {
      if (error?.field) {
        return `${error.field} ${error?.remarks}`;
      } else if (typeof error.remarks === 'string') {
        return `${error?.remarks}`;
      } else if (error?.remarks && typeof error.remarks === 'object') {
        return `${error?.remarks?.name}`;
      }
      return `${error?.message}`;
    });

    arrError && arrError.unshift(payload?.error_message);

    const cleanErrors = sanitizeErrors(arrError);

    return {
      errMsg: cleanErrors.join('</br>'),
      errLevel: payload?.level || status || 0,
      severity,
    };
  }

  return {
    errMsg:
      payload?.error_message ||
      payload?.message ||
      baseQueryReturnValue?.error ||
      'An unexpected error occurred',
    errLevel: payload?.level || status || 0,
    severity,
  };
};

/**
 * Handles fetch-specific API errors and returns appropriate error information
 * @param baseQueryReturnValue - The return value from the base query
 * @returns An object containing error message and error level
 */
export const fetchHandleError = (
  baseQueryReturnValue: any,
): { errMsg: string; errLevel: number } | string => {
  if (!baseQueryReturnValue) {
    return { errMsg: 'Server Error', errLevel: 0 };
  }

  // Support both wrapped and unwrapped shapes
  const payload = baseQueryReturnValue?.data ?? baseQueryReturnValue;
  const status = baseQueryReturnValue?.status ?? payload?.status;

  if (!payload || !status) {
    return { errMsg: 'Server Error', errLevel: 0 };
  }

  if (status >= 500 && status < 600) {
    return { errMsg: payload?.message || 'Server Error', errLevel: 500 };
  }

  //  (old and new shape)
  const apiErrorMessage =
    (payload?.['error message'] as string) ||
    (payload?.error_message as string) ||
    payload?.message ||
    (baseQueryReturnValue?.message as string); // new shape for message

  const apiErrorLevel: string =
    baseQueryReturnValue?.errorLevel ?? payload?.errorLevel;

  const apiErrors = payload?.errors;

  if (
    tokenError.includes(apiErrorMessage?.toLowerCase()) &&
    Array.isArray(apiErrors) &&
    apiErrors?.some(
      ({ error }: any) =>
        error?.remarks?.name?.includes('TokenExpiredError') ||
        error?.remarks?.includes('Session Expired'),
    )
  ) {
    const status = localStorage.getItem(hashName('expiredToken'));
    if (status == '0') {
      var x = window.open('', 'myWindow', 'width=1px,height=1px');
      x?.localStorage.setItem(hashName('expiredToken'), '1');
      x?.close();
      return 'Expired Token';
    }
    return 'Expired Token';
  }

  // errors as array
  if (Array.isArray(apiErrors) && apiErrors.length > 0) {
    const arrError = apiErrors.map(({ error }: any) => {
      if (error?.field) return `${error.field} ${error?.remarks}`;
      else if (error?.remarks) return `${error?.remarks}`;
      return null;
    });

    arrError.unshift(payload?.error_message);

    return {
      errMsg: arrError
        .filter(
          (item: string | null, index: number) =>
            arrError.indexOf(item) === index,
        )
        .filter(Boolean)
        .join('</br>'),
      errLevel: apiErrorLevel === 'warning' ? 1 : payload?.level || 0,
    };
  }

  // Flat message shape: { status, message, title, errorLevel, errorCode } — old or new
  if (apiErrorMessage) {
    return {
      errMsg: apiErrorMessage,
      errLevel: apiErrorLevel === 'warning' ? 1 : payload?.level || 0,
    };
  }

  return { errMsg: 'An unexpected error occurred', errLevel: 0 };
};
